import { useSyncExternalStore } from 'react';

/**
 * Density preference — chrome compactness on top of the existing fluid
 * scale. Values match the convention used by Cloudscape / Material / SAP
 * Fiori so the names will read as obvious to anyone landing here cold.
 *
 *   comfortable  default  more breathing room, the look you saw before
 *   cozy         medium   ~15% tighter paddings + section gaps
 *   compact      tight    ~30% tighter, for power users + data views
 *
 * LocalStorage-backed, framework-agnostic, applies `data-density` on
 * `<html>` so the CSS variants in globals.css can hook in. The whole
 * thing is client-only on purpose — no server schema change, no API,
 * no migration. Promote to user_preferences when product wants the
 * setting to follow the user across devices.
 */
export type Density = 'comfortable' | 'cozy' | 'compact';

const STORAGE_KEY = 'ct-density';
const DEFAULT_DENSITY: Density = 'comfortable';

function isDensity(v: unknown): v is Density {
  return v === 'comfortable' || v === 'cozy' || v === 'compact';
}

function readFromStorage(): Density {
  if (typeof localStorage === 'undefined') return DEFAULT_DENSITY;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isDensity(raw) ? raw : DEFAULT_DENSITY;
  } catch {
    return DEFAULT_DENSITY;
  }
}

let current: Density = readFromStorage();
const listeners = new Set<() => void>();

function notify(): void {
  for (const l of listeners) l();
}

function applyToRoot(d: Density): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset['density'] = d;
}

/** Run once at boot so the dataset is set before first paint. */
export function initDensity(): void {
  applyToRoot(current);
}

export function getDensity(): Density {
  return current;
}

export function setDensity(d: Density): void {
  if (current === d) return;
  current = d;
  applyToRoot(d);
  try {
    localStorage.setItem(STORAGE_KEY, d);
  } catch {
    /* quota / private mode — runtime state still updated in-memory */
  }
  notify();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/**
 * React hook — re-renders when density changes. Use this in components
 * that need to react to density (e.g. the settings ToggleGroup); most of
 * the app reacts purely through CSS via `data-density` on `<html>`.
 */
export function useDensity(): Density {
  return useSyncExternalStore(subscribe, getDensity, () => DEFAULT_DENSITY);
}
